import React, { memo, useContext, useEffect, useMemo } from 'react'

import useEnhancedModelCompatibility from 'Pods/ThreeDStudio/Hooks/useEnhancedModelCompatibility'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import AIEngineSelector from 'Pods/ThreeDStudio/Shared/AIEngineSelector/AIEngineSelector'
import GenerateArea from 'Pods/ThreeDStudio/Shared/GenerateArea/GenerateArea'
import { getHiddenEnhancedProviders } from 'Pods/ThreeDStudio/Shared/Utils/getHiddenEnhancedProviders'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setIsSmartLowPoly } from 'Pods/ThreeDStudio/Store/actions/remeshSettings'
import { ACTION } from 'Pods/ThreeDStudio/Store/constants'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import ExampleSection from './Components/ExampleSection/ExampleSection'
import FaceLevelSelector from './Components/FaceLevelSelector/FaceLevelSelector'
import PolygonTypeSelector from './Components/PolygonTypeSelector/PolygonTypeSelector'
import SmartLowPolySelector from './Components/SmartLowPolySelector/SmartLowPolySelector'

function RemeshTab() {
  const { enableMeshyRemesh, pageConfigLoaded } = useContext(ThreeDStudioPageContext)
  const t = useTranslation()
  const { isModelIncompatible, tooltipMessage } = useEnhancedModelCompatibility()
  const isImageUploading = useThreeDStudioStore(state => state.ui?.isImageUploading)
  const isSmartLowPoly = useThreeDStudioStore(state => state.remeshSettings.isSmartLowPoly)
  const hiddenProviders = useMemo(
    () => getHiddenEnhancedProviders(ACTION.MESH_SIMPLIFICATION, { enableMeshyRemesh }),
    [enableMeshyRemesh]
  )

  // Meshy is the only provider for the standard (non smart-low-poly) mode, so
  // while the enable_meshy_remesh flag hides it the toggle is forced on -
  // otherwise the mode would be left with no eligible provider at all (its
  // schema resolves to null and every uploaded format reads as unsupported).
  // Depends on isSmartLowPoly so a later reset to the false default (e.g.
  // clearModelSettings when re-uploading from an opened-model route) is
  // re-corrected, not just the mount value. Waits for pageConfig so a flag-on
  // user's default (toggle off) is not clobbered during the pre-config window
  // where every flag still reads false.
  useEffect(() => {
    if (pageConfigLoaded && !enableMeshyRemesh && !isSmartLowPoly) {
      setIsSmartLowPoly(true)
    }
  }, [pageConfigLoaded, enableMeshyRemesh, isSmartLowPoly])

  return (
    <>
      <div className="enhance-model-panel__tab remesh-tab">
        <span className="enhance-model-panel__title cgt-heading-50">{t('viewerSidebar.enhance.remesh.title')}</span>
        <div className="enhance-model-panel__settings">
          {enableMeshyRemesh && <SmartLowPolySelector />}
          <PolygonTypeSelector />
          <FaceLevelSelector />
          <AIEngineSelector isEnhanced hiddenProviders={hiddenProviders} />
        </div>
        <ExampleSection />
      </div>
      <GenerateArea
        generateLabel={t('viewerSidebar.enhance.remesh.generateLabel')}
        generatedLabel={t('viewerSidebar.enhance.common.done')}
        generatingLabel={t('viewerSidebar.enhance.common.processing')}
        isEnhancementMode
        isGenerateDisabled={isImageUploading || isModelIncompatible}
        tooltipMessage={tooltipMessage}
        withIcon={false}
      />
    </>
  )
}

export default memo(RemeshTab)
